/** One visibility observer per renderer, rather than one native observer per page.
 * Page hosts retain their geometry; image ownership stays with each ReaderPage. */
const listeners = new Map<Element, (visible: boolean) => void>();
let observer: IntersectionObserver | undefined;

export function observeReaderVisibility(
  element: Element,
  listener: (visible: boolean) => void,
): () => void {
  if (listeners.has(element)) throw new Error('Reader page already has a visibility owner.');
  if (!observer) {
    const owned = new IntersectionObserver(
      (entries) => {
        // A queued notification from a retired observer cannot reach a new owner.
        if (observer !== owned) return;
        for (const entry of entries) listeners.get(entry.target)?.(entry.isIntersecting);
      },
      { rootMargin: '1200px 0px' },
    );
    observer = owned;
  }
  const owned = observer;
  listeners.set(element, listener);
  owned.observe(element);
  return () => {
    if (listeners.get(element) !== listener) return;
    listeners.delete(element);
    owned.unobserve(element);
    if (!listeners.size && observer === owned) {
      owned.disconnect();
      observer = undefined;
    }
  };
}
